import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  Truck,
  Package,
  Trash2,
  CheckCircle,
  Check,
  ChevronsUpDown
} from 'lucide-react';

export default function PurchaseOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState({ item_id: '', quantity: 1, unit_cost: 0 });
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [itemSearchOpen, setItemSearchOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, warehousesRes, itemsRes, suppliersRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/warehouses'),
        api.get('/items'),
        api.get('/suppliers')
      ]);
      setOrders(ordersRes.data);
      setWarehouses(warehousesRes.data);
      setItems(itemsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const addLine = () => {
    if (!currentLine.item_id || currentLine.quantity <= 0 || currentLine.unit_cost <= 0) {
      toast.error('Please fill all fields');
      return;
    }
    
    const item = items.find(i => i.id === currentLine.item_id);
    setLines([...lines, {
      item_id: currentLine.item_id,
      item_sku: item.sku,
      item_name: item.name,
      quantity: currentLine.quantity,
      unit_cost: currentLine.unit_cost,
      line_total: currentLine.quantity * currentLine.unit_cost
    }]);
    setCurrentLine({ item_id: '', quantity: 1, unit_cost: 0 });
  };

  const removeLine = (index) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedWarehouse || lines.length === 0) {
      toast.error('Please select warehouse and add items');
      return;
    }
    
    try {
      await api.post('/purchase-orders', {
        warehouse_id: selectedWarehouse,
        supplier_name: supplierName,
        lines: lines.map(l => ({ item_id: l.item_id, quantity: l.quantity, unit_cost: l.unit_cost })),
        expected_date: expectedDate || null,
        notes: notes
      });
      toast.success('Purchase Order created!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const handleReceive = async (poId) => {
    try {
      await api.post(`/purchase-orders/${poId}/receive`);
      toast.success('PO received into inventory!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive PO');
    }
  };

  const resetForm = () => {
    setSelectedWarehouse('');
    setSupplierName('');
    setLines([]);
    setCurrentLine({ item_id: '', quantity: 1, unit_cost: 0 });
    setExpectedDate('');
    setNotes('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', minimumFractionDigits: 2 }).format(value).replace('JMD', 'J$');
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : '-';

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-[#34C759]/10 text-[#34C759]';
      case 'partial': return 'bg-[#FF9500]/10 text-[#FF9500]';
      case 'submitted': return 'bg-[#007AFF]/10 text-[#007AFF]';
      case 'cancelled': return 'bg-[#FF3B30]/10 text-[#FF3B30]';
      default: return 'bg-[#8E8E93]/10 text-[#8E8E93]';
    }
  };

  const subtotal = lines.reduce((sum, l) => sum + l.line_total, 0);

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">PURCHASE ORDERS</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Manage incoming inventory orders</p>
        </div>
        <Button className="bg-[#007AFF] hover:bg-[#0066CC]" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-po-btn">
          <Plus className="w-4 h-4 mr-2" />New PO
        </Button>
      </div>

      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#007AFF]" />Purchase Orders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Truck className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No purchase orders yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">PO #</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Supplier</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Warehouse</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-center">Items</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Total</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Expected</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((po) => (
                  <TableRow key={po.id} className="border-b border-[#E5E5EA]">
                    <TableCell className="font-mono font-medium">{po.po_number}</TableCell>
                    <TableCell>{po.supplier_name || '-'}</TableCell>
                    <TableCell>{po.warehouse_name}</TableCell>
                    <TableCell className="text-center">{po.lines?.length || 0}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(po.subtotal)}</TableCell>
                    <TableCell><Badge className={getStatusColor(po.status)}>{po.status}</Badge></TableCell>
                    <TableCell>{formatDate(po.expected_date)}</TableCell>
                    <TableCell>
                      {po.status === 'draft' && (
                        <Button size="sm" variant="outline" onClick={() => handleReceive(po.id)} data-testid={`receive-po-${po.id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" />Receive
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#007AFF]" />New Purchase Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Warehouse *</Label>
                <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                  <SelectTrigger><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((wh) => (<SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Supplier name" />
              </div>
            </div>

            <Card className="border border-dashed border-[#007AFF]/30 bg-[#007AFF]/5">
              <CardContent className="p-4">
                <Label className="text-sm font-medium mb-3 block">Add Line Item</Label>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Popover open={itemSearchOpen} onOpenChange={setItemSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-expanded={itemSearchOpen} className="w-full justify-between font-normal" data-testid="po-item-search-btn">
                          {currentLine.item_id ? (() => { const it = items.find(i => i.id === currentLine.item_id); return it ? `${it.sku} - ${it.name}` : 'Select item'; })() : 'Search items...'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search by name or SKU..." data-testid="po-item-search-input" />
                          <CommandList>
                            <CommandEmpty>No items found.</CommandEmpty>
                            <CommandGroup>
                              {items.map((item) => (
                                <CommandItem
                                  key={item.id}
                                  value={`${item.sku} ${item.name}`}
                                  onSelect={() => {
                                    setCurrentLine({ ...currentLine, item_id: item.id });
                                    setItemSearchOpen(false);
                                  }}
                                  data-testid={`po-item-option-${item.id}`}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${currentLine.item_id === item.id ? 'opacity-100' : 'opacity-0'}`} />
                                  <span className="font-mono text-xs mr-2">{item.sku}</span>
                                  <span className="truncate">{item.name}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-2">
                    <Input type="number" placeholder="Qty" value={currentLine.quantity} onChange={(e) => setCurrentLine({ ...currentLine, quantity: parseInt(e.target.value) || 1 })} min="1" />
                  </div>
                  <div className="col-span-3">
                    <Input type="number" placeholder="Unit Cost" value={currentLine.unit_cost} onChange={(e) => setCurrentLine({ ...currentLine, unit_cost: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
                  </div>
                  <div className="col-span-2">
                    <Button type="button" className="w-full bg-[#007AFF]" onClick={addLine}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {lines.length > 0 && (
              <Card className="border border-[#E5E5EA]">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Item</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Cost</TableHead>
                        <TableHead className="text-xs text-right">Total</TableHead>
                        <TableHead className="text-xs w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((line, index) => (
                        <TableRow key={index}>
                          <TableCell className="py-2"><p className="font-medium text-sm">{line.item_name}</p><p className="text-xs text-[#0A0A0A]/50">{line.item_sku}</p></TableCell>
                          <TableCell className="text-right font-mono text-sm">{line.quantity}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(line.unit_cost)}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-medium">{formatCurrency(line.line_total)}</TableCell>
                          <TableCell><Button variant="ghost" size="sm" onClick={() => removeLine(index)} className="text-[#FF3B30] h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="p-4 border-t border-[#E5E5EA] flex justify-between">
                    <span className="font-medium">Total:</span>
                    <span className="font-mono font-bold">{formatCurrency(subtotal)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expected Date</Label>
                <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes..." rows={2} />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#007AFF]" disabled={lines.length === 0}>Create PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
